// Simple YouTube Modal Component
class YouTubeModal extends HTMLElement {
  constructor() {
    super();
    
    this.videoContainer = this.querySelector('.modal-video__video');
    this.closeButton = this.querySelector('[id^="ModalClose-"]');
    
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.hide());
    }
  }

  show(opener) {
    // Get the YouTube link from the opener
    const youtubeLink = opener.getAttribute('data-youtube-link');
    
    if (youtubeLink && this.videoContainer) {
      this.loadYouTubeVideo(youtubeLink);
    }
    
    // Show the modal
    this.setAttribute('open', '');
    document.documentElement.classList.add('overflow-hidden');
    
    // Focus the modal itself
    this.focus();
  }

  hide() {
    // Clear the video when closing
    if (this.videoContainer) {
      this.videoContainer.innerHTML = '';
    }
    
    // Hide the modal
    this.removeAttribute('open');
    document.documentElement.classList.remove('overflow-hidden');
  }

  loadYouTubeVideo(youtubeLink) {
    // Check if video container exists
    if (!this.videoContainer) {
      console.error('Video container not found in modal');
      return;
    }
    
    // Extract video ID from various YouTube URL formats
    const videoId = this.extractYouTubeVideoId(youtubeLink);
    
    if (videoId) {
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
      iframe.width = '100%';
      iframe.height = '100%';
      iframe.frameBorder = '0';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.title = 'YouTube video player';
      
      // Clear previous content and add new iframe
      this.videoContainer.innerHTML = '';
      this.videoContainer.appendChild(iframe);
    } else {
      // Fallback for invalid YouTube links
      this.videoContainer.innerHTML = '<p style="text-align: center; padding: 2rem; color: rgba(var(--color-foreground), 0.6);">Invalid YouTube link</p>';
    }
  }

  extractYouTubeVideoId(url) {
    if (!url) return null;
    
    // Handle various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/,
      /youtu\.be\/([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return null;
  }
}

// Register the custom element
if (!customElements.get('youtube-modal')) {
  customElements.define('youtube-modal', YouTubeModal);
}

// Add event listeners for YouTube trigger buttons
document.addEventListener('DOMContentLoaded', function() {
  // Find all YouTube trigger buttons and add direct event listeners
  const youtubeButtons = document.querySelectorAll('.youtube-trigger');
  
  youtubeButtons.forEach(button => {
    button.addEventListener('click', function(event) {
      event.preventDefault();
      event.stopPropagation();
      
      const youtubeLink = this.getAttribute('data-youtube-link');
      const modalId = this.getAttribute('data-modal-id') || 'YouTubeModal';
      
      if (youtubeLink) {
        openYouTubeModal(youtubeLink, modalId);
      }
    });
  });
});

// Utility function to open YouTube modal programmatically
function openYouTubeModal(youtubeLink, modalId = 'YouTubeModal') {
  const modal = document.getElementById(modalId);
  
  if (modal && typeof modal.show === 'function') {
    // Create a temporary opener element with the YouTube link
    const tempOpener = document.createElement('div');
    tempOpener.setAttribute('data-youtube-link', youtubeLink);
    modal.show(tempOpener);
  } else {
    console.error(`YouTube modal with ID '${modalId}' not found or show method not available`);
  }
}

// Make function globally available
if (typeof window !== 'undefined') {
  window.openYouTubeModal = openYouTubeModal;
}
